<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useTranslation } from 'i18next-vue';
import Button from 'primevue/button';
import {
  formatAmountWithCustomerConfig,
  formatDateOnly,
  shouldIgnoreRowClick,
} from '@peanuds/shared';
import { mapRowToDialogSelection, type TransactionDetailsDialogSelection } from '@peanuds/modules';
import { useCustomerConfig } from '@peanuds/app-shell';
import {
  FileBlankIcon as EmptyIcon,
  ChartBarsVertical2Icon as ChartBarVerticalIcon,
  UiDataTable,
} from '@peanuds/ui';
import { useLazyLoadedRowsPagination } from '@peanuds/composables';
import { useControlSpendingsTableFeed } from '../composables/useControlSpendingsTableFeed';
import { useControlSpendingsTableRows } from '../composables/useControlSpendingsTableRows';
import { getControlSpendingColumns } from '../internal/control-spendings-table.columns';
import type { FilterableControlSpendingTableRow } from '../internal/control-spendings-table.filters';
import type { ControlSpendingsTableFilter } from '../internal/control-spendings-table.types';

export type TableFilter = ControlSpendingsTableFilter;

const emit = defineEmits<{
  'transaction-click': [selection: TransactionDetailsDialogSelection];
}>();

const { search = '', filter = 'all-transactions' } = defineProps<{
  search?: string;
  filter?: TableFilter;
}>();

const { t } = useTranslation();
const router = useRouter();
const { currency, dateTime } = useCustomerConfig();

const {
  pageSize,
  transferDateFrom,
  resolvedTransactionAccountIds,
  transactions,
  isTransactionsLoading,
  transactionsError,
  hasMore,
  loadMore,
} = useControlSpendingsTableFeed({
  filter: () => filter,
});

const emptyMessage = computed(() =>
  t('common.transactions_empty_for_period', {
    from: formatDateOnly(transferDateFrom, dateTime.value?.locale),
    to: formatDateOnly(new Date(), dateTime.value?.locale),
  }),
);

const { filteredRows, totalRecords, hasReferenceDetails } = useControlSpendingsTableRows({
  transactions,
  filter: () => filter,
  search: () => search,
});

const hasTransactionsError = computed(() => transactionsError.value !== undefined);
const {
  currentPageStart,
  visibleRows,
  displayTotalRecords: tableTotalRecords,
  handlePageChange,
} = useLazyLoadedRowsPagination({
  rows: filteredRows,
  pageSize,
  hasMore,
  loadMore,
  resetSources: [() => filter, () => search, () => resolvedTransactionAccountIds.value.join(',')],
});

const translatedColumns = computed(() => {
  return getControlSpendingColumns(hasReferenceDetails.value, {
    formatter: (row) => formatAmountWithCustomerConfig(row, currency.value.transactionAmount),
  }).map((column) => ({
    ...column,
    header: t(column.header),
  }));
});

const isLargeScreen = ref(false);
const updateMedia = () => {
  isLargeScreen.value = window.matchMedia('(min-width: 80rem)').matches;
};

onMounted(() => {
  updateMedia();
  window.addEventListener('resize', updateMedia);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateMedia);
});

const handleOpenAnalytics = () => {
  const accountId = resolvedTransactionAccountIds.value[0];

  router.push({
    name: 'analytics',
    query: accountId ? { accountId } : undefined,
  });
};

const handleRowClick = (event: {
  originalEvent?: Event;
  data: FilterableControlSpendingTableRow;
}) => {
  if (shouldIgnoreRowClick(event.originalEvent)) return;
  emit('transaction-click', mapRowToDialogSelection(event.data));
};
</script>

<template>
  <div class="cs-table">
    <div
      v-if="!isTransactionsLoading && hasTransactionsError && totalRecords === 0"
      class="cs-table__empty"
    >
      <div class="cs-table__empty-icon">
        <EmptyIcon />
      </div>
      <p class="cs-table__empty-text">{{ t('controlSpending.table.load_error') }}</p>
    </div>

    <template v-else-if="isTransactionsLoading || totalRecords > 0 || hasMore">
      <div v-if="!isTransactionsLoading && hasTransactionsError" class="cs-table__warning">
        {{ t('controlSpending.table.load_error') }}
      </div>

      <UiDataTable
        :loading="isTransactionsLoading"
        :columns="translatedColumns"
        sortField="dateAndTime"
        :value="visibleRows"
        :first="currentPageStart"
        :rows="pageSize"
        :total-records="tableTotalRecords"
        :show-first-last-page-links="false"
        lazy
        class="cs-table__grid"
        data-testid="dashboard-control-spendings-table"
        scrollable
        scrollHeight="flex"
        @page="handlePageChange"
        @row-click="handleRowClick"
      >
        <template #paginatorend>
          <div class="cs-table__paginator-actions">
            <Button
              class="cs-table__analytics-button"
              :label="isLargeScreen ? t('controlSpending.open_analytics') : undefined"
              severity="info"
              rounded
              outlined
              @click="handleOpenAnalytics"
            >
              <template #icon>
                <ChartBarVerticalIcon class="p-button-icon-right" />
              </template>
            </Button>
          </div>
        </template>
      </UiDataTable>
    </template>

    <div v-else class="cs-table__empty">
      <div class="cs-table__empty-icon">
        <EmptyIcon />
      </div>
      <p class="cs-table__empty-text">{{ emptyMessage }}</p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.cs-table {
  display: flex;
  min-height: 0;
  flex-direction: column;
  height: 100%;

  &__grid {
    flex: 1;
    min-height: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  :deep(.p-datatable-table-container),
  :deep(.p-datatable-wrapper) {
    flex: 1;
    min-height: 0;
    overflow: auto;
  }

  :deep(.ui-table-skeleton) {
    flex: 1;
    width: 100%;
  }

  :deep(.ui-table-skeleton .p-datatable),
  :deep(.ui-table-skeleton .p-datatable-table) {
    width: 100%;
  }

  :deep(.p-datatable-paginator-bottom) {
    margin-inline: -$static-space-4;
    padding-inline: $static-space-4;
  }

  &__analytics-button {
    padding: $static-space-2 $static-space-2;
    border-radius: var(--p-border-radius-absolut);
    color: #0ea5e9;
    font-size: $static-text-md;
    font-weight: $font-medium;
    letter-spacing: var(--p-letter-spacing-body);
    margin-inline: -9px;
  }

  &__paginator-actions {
    display: flex;
    gap: $static-space-2;
    align-items: center;
  }

  &__warning {
    margin-bottom: $static-space-2;
    padding: $static-space-2 $static-space-4;
    border-radius: var(--p-border-radius-xl);
    background-color: var(--p-orange-50);
    color: var(--p-orange-700);
    font-size: $static-text-sm;
    font-weight: $font-medium;
  }

  &__amount-header :deep(.p-datatable-column-header-content) {
    justify-content: flex-end;
  }

  &__amount-header :deep(.p-datatable-column-title) {
    margin-left: auto;
  }

  :deep(.cs-table__reference-header),
  :deep(.cs-table__reference-cell) {
    width: 6rem;
    max-width: 6rem;
  }

  :deep(.cs-table__reference-cell) {
    overflow: hidden;
  }

  :deep(.p-datatable-tbody > tr > td) {
    padding: 10.5px $static-space-4;
  }

  :deep(.p-datatable-tbody > tr) {
    cursor: pointer;
  }

  :deep(.cs-table__date-header.p-datatable-sortable-column),
  :deep(.cs-table__date-header.p-datatable-sortable-column:not(.p-datatable-column-sorted):hover),
  :deep(.cs-table__date-header.p-datatable-column-sorted) {
    background: var(--p-datatable-header-cell-background);
    color: var(--p-datatable-header-cell-color);
  }

  :deep(.cs-table__date-header .p-datatable-column-title) {
    color: inherit;
  }

  :deep(.cs-table__date-header .p-datatable-sort-icon) {
    display: none;
  }

  &__empty {
    display: grid;
    grid-template-columns: $static-icon-xl minmax(0, 1fr) $static-icon-xl;
    flex: 0 0 auto;
    margin-top: $static-space-2;
    padding: $static-space-6 $static-space-4;
    gap: $static-space-3;
    align-items: center;
    justify-content: center;
    background-color: #f8fafc;
    border-radius: $radius-2xl;

    &-icon {
      flex-shrink: 0;
      width: $static-space-10;
      height: $static-space-10;
      border-radius: var(--p-border-radius-absolut);
      background-color: #cbd5e1;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &-text {
      margin: 0;
      color: #334155;
      font-size: $static-text-md;
      line-height: $leading-tight;
      text-align: center;
      white-space: pre-line;
      overflow-wrap: anywhere;
    }
  }
}
</style>
